import { useState, useEffect } from "react";
import store from "store";
import { commonActions } from "store/common";
import { Client, ClientUpdatePayload } from "../types/client";
import { fetchClientsApi, updateClientApi } from "../api/clientApi";

/**
 *
 * @param page - The current page number for fetching clients
 */
/**
 * Custom hook to fetch and manage a paginated list of clients.
 * @returns {object} An object containing the following properties:
 * - `clients` {Client[]} - The list of clients fetched for the current page.
 * - `hasMore` {boolean} - A boolean indicating if there are more clients to fetch.
 * @example
 * const { clients, hasMore } = useClientList(1);
 * @remarks
 * This hook fetches clients in pages of 10. It uses a placeholder data fetching logic
 * which should be replaced with an actual API call. The `created_at` and `updated_at`
 * fields of each client are converted to `Date` objects.
 */
const useClientList = () => {
  const [clients, setClients] = useState<Client[]>([]);
  const [hasMore, setHasMore] = useState(0);
  const [totalRecord, setTotalRecord] = useState(0);
  const [query, setQuery] = useState({
    client_cd: "",
    client_cd_or_name: "",
    limit: 50,
    offset: 1,
    pref_cd: "",
    status_flag: 1,
  });
  const [tempQuery, setTempQuery] = useState({
    client_cd_or_name: "",
    pref_cd: "",
    status_flag: 1,
  });
  const [isFetching, setIsFetching] = useState(false);

  const fetchData = async (refetch = false) => {
    const payload = {
      ...(query.client_cd.length > 0 ? { client_cd: query.client_cd } : {}),
      ...(query.client_cd_or_name.length > 0 ? { client_cd_or_name: query.client_cd_or_name } : {}),
      ...(query.pref_cd.length > 0 ? { pref_cd: query.pref_cd } : {}),
      ...(query.status_flag === 1 ? { status_flag: 1 } : {}),
      limit: query.limit,
      offset: refetch ? 1 : query.offset,
    };
    setIsFetching(true);
    try {
      const { list, total } = await fetchClientsApi(payload);
      if (query.offset > 1 && !refetch) {
        setClients((prevUsers) => [...prevUsers, ...list]);
      } else {
        setClients(list);
      }
      setTotalRecord(total);
      if (query.offset * query.limit >= total) {
        setHasMore(0);
      } else {
        setHasMore(hasMore + 1);
      }
    } catch (error) {
      console.error("Error fetching clients:", error);
    } finally {
      setIsFetching(false);
    }
  };

  const handleSubmitUpdate = async (clientUpdate: ClientUpdatePayload) => {
    if (!clientUpdate) return;
    setIsFetching(true);
    store.dispatch(commonActions.setLoading());
    try {
      await updateClientApi(clientUpdate);
      await fetchData(true);
    } finally {
      setIsFetching(false);
      store.dispatch(commonActions.removeLoading());
    }
  };

  useEffect(() => {
    fetchData();
  }, [query]);

  return {
    clients,
    isFetching,
    hasMore,
    totalRecord,
    setQuery,
    query,
    setTempQuery,
    tempQuery,
    setIsFetching,
    handleSubmitUpdate,
  };
};

export default useClientList;
